import { IonButton, IonButtons, IonContent, IonHeader, IonModal, IonTitle, IonToolbar } from '@ionic/react';
import { useEffect, useState } from 'react';
import { palette } from '../design.js';
import { LATE_ENTRY_DEFAULT_TIME, formatDayHeading } from '../../lib/haccp/lateEntry.js';

interface LateTimeModalProps {
  isOpen: boolean;
  /** Titolo del modale (es. "Recupero controllo"). */
  title: string;
  /** Chiave-giorno 'YYYY-MM-DD' che si sta recuperando (mostrata leggibile). */
  dayKey: string | null;
  /** Sottotitolo: cosa si sta registrando (es. "Frigo cucina" o "3 voci"). */
  subtitle?: string;
  /** Ora di default 'HH:mm'. */
  defaultTime?: string;
  onConfirm: (hhmm: string) => void;
  onDismiss: () => void;
}

// T-333 — chiede all'operatore l'ORA REALE in cui ha eseguito il controllo/la
// pulizia nel giorno che sta recuperando. Niente orario casuale: il dato resta
// veritiero e difendibile davanti all'ASL (è solo un inserimento in ritardo).
export function LateTimeModal({
  isOpen,
  title,
  dayKey,
  subtitle,
  defaultTime,
  onConfirm,
  onDismiss,
}: LateTimeModalProps): JSX.Element {
  const [time, setTime] = useState(defaultTime ?? LATE_ENTRY_DEFAULT_TIME);

  // Reset all'apertura: ogni recupero riparte dall'ora di default suggerita.
  useEffect(() => {
    if (isOpen) setTime(defaultTime ?? LATE_ENTRY_DEFAULT_TIME);
  }, [isOpen, defaultTime]);

  const valid = /^\d{1,2}:\d{2}$/.test(time);

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onDismiss}
      initialBreakpoint={0.5}
      breakpoints={[0, 0.5, 0.9]}
    >
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={onDismiss}>Annulla</IonButton>
          </IonButtons>
          <IonTitle>{title}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent style={{ '--background': palette.bg } as React.CSSProperties}>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div
            style={{
              background: palette.card,
              border: `1px solid ${palette.border}`,
              borderRadius: 12,
              padding: '12px 14px',
            }}
          >
            <div style={{ fontSize: 12, color: palette.textFaint }}>Stai recuperando</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: palette.text, marginTop: 2 }}>
              {dayKey ? formatDayHeading(dayKey) : '—'}
            </div>
            {subtitle && (
              <div style={{ fontSize: 13, color: palette.textFaint, marginTop: 4 }}>{subtitle}</div>
            )}
          </div>

          <div>
            <label
              htmlFor="late-time"
              style={{ fontSize: 13, fontWeight: 600, color: palette.text, display: 'block', marginBottom: 8 }}
            >
              A che ora l'hai eseguito?
            </label>
            <input
              id="late-time"
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              step={60}
              style={{
                width: '100%',
                padding: '14px 12px',
                borderRadius: 12,
                border: `1px solid ${palette.borderStrong}`,
                background: palette.card,
                color: palette.text,
                fontSize: 20,
                fontWeight: 600,
                textAlign: 'center',
              }}
            />
            <div style={{ fontSize: 12, color: palette.textFaint, marginTop: 8, lineHeight: 1.4 }}>
              Inserisci l'orario reale: la registrazione viene segnata in quel
              giorno (registrata in ritardo, ma con il dato vero).
            </div>
          </div>

          <IonButton
            expand="block"
            disabled={!valid}
            onClick={() => valid && onConfirm(time)}
          >
            Registra
          </IonButton>
        </div>
      </IonContent>
    </IonModal>
  );
}
